(function () {
  function isShopifyPixelSandboxWindow() {
    try {
      const frameName = String(window.name || "");
      const href = String(window.location.href || "");
      return /web-pixel-sandbox|\/web-pixels@|\/wpm@|\/sandbox\/modern/i.test(
        `${frameName} ${href}`,
      );
    } catch (e) {
      return false;
    }
  }

  if (isShopifyPixelSandboxWindow()) return;
  if (window.__shopifyCartRequestManagerInstalled) return;
  window.__shopifyCartRequestManagerInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  const BATCH_DELAY_MS = 5000;
  const MICRO_BATCH_MS = 30;
  const SEND_GRACE_MS = 20;
  let DEBUG = false;

  const state = {
    cartSnapshot: null,
    cartInflight: null,
    pendingMutationCount: 0,
    mutationGate: null,
    cartVersion: 0,
    addQueue: [],
    updateQueue: [],
    addTimer: null,
    updateTimer: null,
  };

  function normalizeUrl(input) {
    try {
      if (typeof input === "string")
        return new URL(input, window.location.href);
      if (input && typeof input.url === "string") {
        return new URL(input.url, window.location.href);
      }
    } catch (e) {
      void e;
    }

    return null;
  }

  function isPath(url, suffix) {
    if (!url) return false;
    const normalize = (path) => (path === "/" ? "/" : path.replace(/\/+$/, ""));
    return normalize(url.pathname) === normalize(suffix);
  }

  function isSameOriginUrl(url) {
    return !!url && url.origin === window.location.origin;
  }

  function getCartRoute(url, method) {
    if (!isSameOriginUrl(url)) return null;

    if ((isPath(url, "/cart.js") || isPath(url, "/cart")) && method === "GET") {
      return "cart";
    }

    if (
      (isPath(url, "/cart/add.js") || isPath(url, "/cart/add")) &&
      method === "POST"
    ) {
      return "add";
    }

    if (isPath(url, "/cart/update.js") && method === "POST") {
      return "update";
    }

    if (
      (isPath(url, "/cart/change.js") || isPath(url, "/cart/change")) &&
      method === "POST"
    ) {
      return "change";
    }

    return null;
  }

  function isPlainObject(value) {
    return (
      !!value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof FormData) &&
      !(value instanceof URLSearchParams)
    );
  }

  function sortObject(value) {
    if (Array.isArray(value)) return value.map(sortObject);
    if (!isPlainObject(value)) return value;

    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const current = value[key];
        if (typeof current !== "undefined") acc[key] = sortObject(current);
        return acc;
      }, {});
  }

  const __sigCache = new WeakMap();

  function signature(item) {
    if (!item || typeof item !== "object") return String(item);

    if (__sigCache.has(item)) {
      return __sigCache.get(item);
    }

    // Fast-path fields
    const id = item.id ?? item.variant_id ?? "";

    const sellingPlan =
      item.selling_plan ??
      item.selling_plan_group_id ??
      item.selling_plan_allocation_id ??
      "";

    let propsSig = "";

    const props = item.properties;
    if (props && typeof props === "object") {
      let str = "";
      for (const k in props) {
        str += k + ":" + props[k] + "|";
      }
      propsSig = str;
    }

    const sig = id + "#" + sellingPlan + "#" + propsSig;

    __sigCache.set(item, sig);
    return sig;
  }

  function parseEntries(entries) {
    const output = {};
    for (const [key, value] of entries) {
      if (key.startsWith("properties[") && key.endsWith("]")) {
        output.properties = output.properties || {};
        output.properties[key.slice(11, -1)] = value;
        continue;
      }

      if (key.startsWith("attributes[") && key.endsWith("]")) {
        output.attributes = output.attributes || {};
        output.attributes[key.slice(11, -1)] = value;
        continue;
      }

      output[key] = value;
    }

    return output;
  }

  function parseFormData(formData) {
    return parseEntries(formData.entries());
  }

  function parseBody(body) {
    if (!body) return {};

    if (body instanceof FormData) return parseFormData(body);
    if (body instanceof URLSearchParams) {
      return parseEntries(body.entries());
    }

    if (typeof body === "string") {
      const str = body.trim();
      if (!str) return {};

      const first = str[0];
      // Fast-path JSON if it looks like JSON
      if (first === "{" || first === "[") {
        try {
          return JSON.parse(str);
        } catch (e) {
          // fallthrough to URLSearchParams fallback
        }
      }

      // If string doesn't contain common URL-encoded separators, bail early
      if (
        str.indexOf("=") === -1 &&
        str.indexOf("&") === -1 &&
        str.indexOf("%") === -1
      )
        return {};

      const params = new URLSearchParams(str);
      const entries = Array.from(params.entries());
      if (!entries.length) return {};
      return parseEntries(entries);
    }

    if (isPlainObject(body)) return body;

    return {};
  }

  function normalizeSections(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    const str = "" + value;
    const out = [];
    let start = 0;
    for (let i = 0; i <= str.length; i++) {
      if (i === str.length || str.charCodeAt(i) === 44) {
        const s = str.slice(start, i).trim();
        if (s) out.push(s);
        start = i + 1;
      }
    }
    return out;
  }

  function sanitizeSectionsList(list) {
    if (!Array.isArray(list)) return [];
    const sanitized = list.map(String).filter(Boolean);

    // Drop the cart-drawer section to save render cost on the cart page —
    // but only when there's no <cart-drawer> element actually on the page.
    // The header's cart-icon-bubble (and its drawer) are present on every
    // page, /cart included, so stripping this unconditionally left the
    // drawer's DOM showing stale contents (e.g. an item just deleted on
    // /cart) until some other page happened to refresh it.
    if (!document.querySelector("cart-drawer")) {
      return sanitized.filter((s) => !/cart[_-]drawer/i.test(s));
    }
    return sanitized;
  }

  function getCartSectionKeys() {
    const el = document.getElementById("main-cart-items");
    const dataId = el && (el.getAttribute("data-id") || el.dataset?.id);
    const itemsKey = dataId ? String(dataId) : "cart-items";
    const footerKey = String(itemsKey).replace(/items$/, "footer");
    return { itemsKey, footerKey };
  }

  function runScriptsInContainer(container) {
    if (!container) return;
    try {
      const scripts = Array.from(container.querySelectorAll("script"));
      scripts.forEach((old) => {
        const s = document.createElement("script");
        for (const attr of old.attributes) {
          s.setAttribute(attr.name, attr.value);
        }
        if (old.textContent) s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
      });
    } catch (e) {
      /* best-effort */
    }
  }

  // Avoid requesting sections when quickly applying cart attributes
  function isPayloadOnlyAttributes(payload) {
    if (!payload || typeof payload !== "object") return false;
    const keys = Object.keys(payload || {});
    return (
      keys.length === 1 &&
      Object.prototype.hasOwnProperty.call(payload, "attributes")
    );
  }

  function createResponseLike({
    bodyText,
    parsedJson,
    status,
    statusText,
    headers,
  }) {
    // Avoid redundant parse/stringify by accepting an already-parsed JSON
    // object when available. Only stringify lazily when `.text()` is
    // requested and no original `bodyText` was provided.
    let _bodyText = typeof bodyText !== "undefined" ? bodyText : null;
    let _parsedJson = typeof parsedJson !== "undefined" ? parsedJson : null;
    let parsedJsonReady = typeof parsedJson !== "undefined";

    return {
      ok: status >= 200 && status < 300,
      status,
      statusText,
      headers: headers || {},
      async text() {
        if (_bodyText == null) {
          // Lazily stringify the parsed JSON only if needed
          if (parsedJsonReady) {
            try {
              _bodyText = JSON.stringify(_parsedJson);
            } catch (e) {
              _bodyText = "";
            }
          } else {
            _bodyText = "";
          }
        }
        return _bodyText;
      },
      async json() {
        if (!parsedJsonReady) {
          parsedJsonReady = true;
          try {
            _parsedJson = _bodyText ? JSON.parse(_bodyText) : {};
          } catch (e) {
            _parsedJson = {};
          }
        }
        return _parsedJson;
      },
      clone() {
        // Prefer passing the parsed JSON to avoid reparsing in clones.
        if (parsedJsonReady) {
          return createResponseLike({
            parsedJson: _parsedJson,
            status,
            statusText,
            headers,
          });
        }
        return createResponseLike({
          bodyText: _bodyText,
          status,
          statusText,
          headers,
        });
      },
    };
  }

  const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

  // Build a genuine Response instance from a cached snapshot so callers
  // (including Shopify's own cart-tracking, which relies on real Response
  // behavior) see the same object shape the native fetch would have
  // returned. Falls back to the hand-rolled response when Response isn't
  // available (older embedded webviews, or this file's test harness).
  function toResponse(snapshot) {
    if (typeof Response === "undefined") return createResponseLike(snapshot);

    try {
      const status = snapshot.status ?? 200;
      const headers = new Headers(snapshot.headers || {});
      // These describe the original (possibly compressed) transfer and no
      // longer match the body we're about to hand back.
      headers.delete("content-encoding");
      headers.delete("content-length");

      const bodyText =
        typeof snapshot.bodyText !== "undefined" && snapshot.bodyText !== null
          ? snapshot.bodyText
          : snapshot.parsedJson
            ? JSON.stringify(snapshot.parsedJson)
            : "";

      return new Response(NULL_BODY_STATUSES.has(status) ? null : bodyText, {
        status,
        statusText: snapshot.statusText || "",
        headers,
      });
    } catch (e) {
      return createResponseLike(snapshot);
    }
  }

  async function readSnapshot(response) {
    const bodyText = await response.text();
    let parsedJson = null;

    try {
      parsedJson = bodyText ? JSON.parse(bodyText) : null;
    } catch (e) {
      parsedJson = null;
    }

    return {
      bodyText,
      parsedJson,
      status: response.status ?? 200,
      statusText: response.statusText ?? "",
      headers: response.headers || {},
    };
  }

  function snapshotToResponse(snapshot) {
    return toResponse({
      bodyText: snapshot.bodyText,
      parsedJson: snapshot.parsedJson,
      status: snapshot.status,
      statusText: snapshot.statusText,
      headers: snapshot.headers,
    });
  }

  // Create a shallow copy of a cart JSON with the top-level `sections`
  // property removed. This prevents saving large HTML snippets into the
  // in-memory cart snapshot while keeping the rest of the payload intact.
  function stripSectionsFromJson(json) {
    if (!json || typeof json !== "object") return json;
    if (Array.isArray(json)) return json.map(stripSectionsFromJson);
    if (!Object.prototype.hasOwnProperty.call(json, "sections")) return json;

    const copy = {};
    for (const k in json) {
      if (k === "sections") continue;
      copy[k] = json[k];
    }
    return copy;
  }

  function isCartSnapshot(value) {
    return (
      isPlainObject(value) &&
      Array.isArray(value.items) &&
      typeof value.item_count !== "undefined"
    );
  }

  function isCompleteCartSnapshot(value) {
    return isCartSnapshot(value) && typeof value.total_price !== "undefined";
  }

  // The flat single-item shape Shopify's /cart/add.js returns for a
  // singular (non-items[]) add: no `items` array, but an id/variant_id and
  // quantity at the top level.
  function isSingleAddedItem(value) {
    return (
      isPlainObject(value) &&
      !Array.isArray(value.items) &&
      (value.id != null || value.variant_id != null) &&
      typeof value.quantity !== "undefined"
    );
  }

  function updateCartSnapshotFromJson(json, responseMeta) {
    if (!isCartSnapshot(json)) return;
    const sanitized = stripSectionsFromJson(json);

    // Try to preserve a sanitized version of the original bodyText when
    // possible, otherwise stringify the sanitized JSON.
    let bodyText;
    if (responseMeta && typeof responseMeta.bodyText !== "undefined") {
      try {
        const parsedFromMeta = responseMeta.bodyText
          ? JSON.parse(responseMeta.bodyText)
          : null;
        const sanitizedParsed = parsedFromMeta
          ? stripSectionsFromJson(parsedFromMeta)
          : sanitized;
        bodyText = JSON.stringify(sanitizedParsed);
      } catch (e) {
        bodyText = JSON.stringify(sanitized);
      }
    } else {
      bodyText = JSON.stringify(sanitized);
    }

    updateCartBubble(sanitized.item_count);
    updateCartTotalFromJson(sanitized);

    state.cartSnapshot = {
      bodyText,
      parsedJson: sanitized,
      status: responseMeta?.status ?? 200,
      statusText: responseMeta?.statusText ?? "",
      headers: responseMeta?.headers || {},
    };
  }

  function appendItemsToSnapshot(items, responseMeta) {
    if (!Array.isArray(items)) return;

    // We don't have enough information to store or update snapshot
    if (!isCompleteCartSnapshot(responseMeta.parsedJson)) {
      // TODO: Let this pass, and below, use proper formula to merge
      // the item into existing snapshot and get total.
      // This will reduce a call on add to cart.
      state.cartSnapshot = null;
      fetchAndCacheCart(); // we must force a call to get cart data for updated total
      return;
    } else if (!state.cartSnapshot || !state.cartSnapshot.parsedJson) {
      const parsed = {
        items: [...items],
        item_count: items.reduce((s, it) => s + Number(it.quantity || 0), 0),
      };
      state.cartSnapshot = {
        bodyText: JSON.stringify(parsed),
        parsedJson: parsed,
        status: responseMeta?.status ?? 200,
        statusText: responseMeta?.statusText ?? "",
        headers: responseMeta?.headers || {},
      };
      applyEmptyState(parsed);
      return;
    }

    // This would need to update the total_price field of the snapshot
    const cart = state.cartSnapshot.parsedJson;
    if (!Array.isArray(cart.items))
      cart.items = Array.isArray(cart.items) ? cart.items : [];

    items.forEach((newItem) => {
      let foundIdx = -1;
      if (newItem.key) {
        foundIdx = cart.items.findIndex((i) => i.key === newItem.key);
      }
      if (
        foundIdx === -1 &&
        (newItem.id != null || newItem.variant_id != null)
      ) {
        const idToMatch = newItem.id ?? newItem.variant_id;
        foundIdx = cart.items.findIndex(
          (i) => (i.id ?? i.variant_id) == idToMatch,
        );
      }

      if (foundIdx !== -1) {
        const existing = cart.items[foundIdx];
        // The total quantity is returned here
        existing.quantity = Number(newItem.quantity || 0);
        existing.line_price =
          Number(existing.line_price || 0) + Number(newItem.line_price || 0);
        existing.final_line_price =
          Number(existing.final_line_price || 0) +
          Number(newItem.final_line_price || newItem.line_price || 0);
        existing.total_discount =
          Number(existing.total_discount || 0) +
          Number(newItem.total_discount || 0);
      } else {
        cart.items.push(newItem);
      }
    });

    cart.item_count = cart.items.reduce(
      (s, it) => s + Number(it.quantity || 0),
      0,
    );

    state.cartSnapshot.parsedJson = cart;
    state.cartSnapshot.bodyText = JSON.stringify(cart);
    state.cartSnapshot.status =
      responseMeta?.status ?? state.cartSnapshot.status;
    state.cartSnapshot.statusText =
      responseMeta?.statusText ?? state.cartSnapshot.statusText;
    state.cartSnapshot.headers =
      responseMeta?.headers || state.cartSnapshot.headers || {};

    applyEmptyState(state.cartSnapshot.parsedJson);
  }

  function updateCartBubble(count) {
    const existingBubble = document.getElementById("cart-icon-bubble");
    const innerBubble = existingBubble?.querySelector(
      ".cart-count-bubble span",
    );
    if (existingBubble && innerBubble) {
      innerBubble.innerText = count;
    }
  }

  function formatMoneyCents(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (e) {
        void e;
      }
    }

    const amount = (Number(cents) || 0) / 100;
    const currency =
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      document.querySelector('meta[name="currency"]')?.content ||
      document.querySelector('meta[name="shop-currency"]')?.content ||
      window.Shopify?.currency?.active ||
      "USD";

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return "$" + amount.toFixed(2);
    }
  }

  function isElementVisible(el) {
    if (!el) return false;
    if (el.hidden || el.getAttribute("aria-hidden") === "true") return false;
    if (
      el.classList?.contains("hidden") ||
      el.classList?.contains("csi-hidden")
    ) {
      return false;
    }

    const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
    return !(
      style &&
      (style.display === "none" || style.visibility === "hidden")
    );
  }

  function parseMoneyCents(value) {
    if (typeof value !== "string") return 0;
    const normalized = value.replace(/[^0-9.,-]/g, "").replace(/,/g, "");
    const amount = Number.parseFloat(normalized);
    if (!Number.isFinite(amount)) return 0;
    return Math.round(amount * 100);
  }

  function cartHasShippingInsurance(json) {
    if (!json || !Array.isArray(json.items)) return false;

    return json.items.some((item) => {
      const props = item && item.properties;
      if (!props || typeof props !== "object") return false;
      return (
        props.__shipping_insurance === true ||
        props.__shipping_insurance === "true"
      );
    });
  }

  function getSelectedShippingInsuranceCents(json) {
    const extension = document.querySelector(
      'div[data-extension="cart-shipping-insurance"]',
    );
    if (!isElementVisible(extension)) return 0;

    const checkbox = extension.querySelector(".csi-lg-checkbox");
    if (!isElementVisible(checkbox) || !checkbox.checked) return 0;
    if (cartHasShippingInsurance(json)) return 0;

    return parseMoneyCents(
      extension.querySelector(".csi-insurance-price")?.textContent || "",
    );
  }

  function updateCartTotalFromJson(json) {
    if (!json || typeof json.total_price === "undefined") return;

    const totalCents =
      Number(json.total_price || 0) + getSelectedShippingInsuranceCents(json);
    const total = formatMoneyCents(totalCents);
    document.querySelectorAll(".totals__total-value").forEach((el) => {
      el.textContent = total;
    });
  }

  function scheduleCartTotalUpdate(json) {
    if (!json || typeof json.total_price === "undefined") return;
    requestAnimationFrame(() => {
      updateCartTotalFromJson(json);
    });
  }

  function updateCartDrawerFromSections(sections) {
    if (!sections) return;

    const SELECTORS = {
      footer: [
        ".drawer__footer",
        ".cart__footer",
        "#main-cart-footer",
        ".totals",
        ".cart_footer",
      ],
      cartTable: ["table.cart-items", "cart-items", ".cart-items"],
      wrapper: [
        "table.cart-items",
        "cart-drawer-items",
        ".drawer__cart-items-wrapper",
        "cart-items",
      ],
    };

    const parser = new DOMParser();

    const queryFirst = (root, selectors) => {
      for (let i = 0; i < selectors.length; i++) {
        const el = root.querySelector(selectors[i]);
        if (el) return el;
      }
      return null;
    };

    const getKey = (row) => {
      if (row.__k) return row.__k;
      const val = row.querySelector("a")?.getAttribute("href") || "";
      row.__k = val;
      return val;
    };

    const canReuseRow = (existing, next) => {
      if (!existing || !next) return false;
      return existing.isEqualNode(next);
    };

    const existingWrapper = queryFirst(document, SELECTORS.wrapper);
    const existingFooter = queryFirst(document, SELECTORS.footer);

    if (!existingWrapper) return;

    let cartUpdated = false;

    for (const key in sections) {
      const html = sections[key];
      if (typeof html !== "string") continue;
      if (!html.includes("<")) continue;

      const doc = parser.parseFromString(html, "text/html");

      // footer
      /*if (!cartUpdated && /^template--\d+__cart-footer$/.test(String(key))) {
        const newFooter = queryFirst(doc, SELECTORS.footer);

        if (newFooter && existingFooter) {
          const clone = newFooter.cloneNode(true);

          requestAnimationFrame(() => {
            existingFooter.replaceWith(clone);
            runScriptsInContainer(clone);
          });
        }
      }*/

      // cart-icon-bubble
      if (!cartUpdated && key === "cart-icon-bubble") {
        const newBubble = doc.getElementById("cart-icon-bubble");
        const existingBubble = document.getElementById("cart-icon-bubble");

        if (newBubble && existingBubble) {
          const bubbleClone = newBubble.cloneNode(true);

          requestAnimationFrame(() => {
            existingBubble.replaceWith(bubbleClone);
            runScriptsInContainer(bubbleClone);
          });
        }
      }

      if (cartUpdated) continue;

      const newTable = queryFirst(doc, SELECTORS.cartTable);
      if (!newTable) continue;

      const currentTable =
        queryFirst(existingWrapper, ["table.cart-items", ".cart-items"]) ||
        (existingWrapper.tagName?.toLowerCase() === "table"
          ? existingWrapper
          : null);

      if (!currentTable) continue;

      const newTbody = newTable.querySelector("tbody");
      const currTbody = currentTable.querySelector("tbody");

      if (!newTbody || !currTbody) {
        if (currentTable.innerHTML !== newTable.innerHTML) {
          requestAnimationFrame(() => {
            currentTable.innerHTML = newTable.innerHTML;
          });
        }
        //continue;
      } else {
        const newRows = Array.from(newTbody.children);
        const currRows = Array.from(currTbody.children);

        const currMap = new Map();
        for (let i = 0, l = currRows.length; i < l; i++) {
          const row = currRows[i];
          currMap.set(row.__k || (row.__k = getKey(row)), row);
        }

        const fragment = document.createDocumentFragment();

        for (let i = 0; i < newRows.length; i++) {
          const next = newRows[i];
          const keyVal = getKey(next);
          const existing = currMap.get(keyVal);

          if (!existing) {
            fragment.appendChild(next.cloneNode(true));
            continue;
          }

          // Reuse only when markup and attributes still match so loading-state
          // classes do not get stuck behind identical text content.
          if (!canReuseRow(existing, next)) {
            const clone = next.cloneNode(true);
            // A quantity/price change replaces the whole row, but the
            // product image itself hasn't changed. Swap in the existing,
            // already-loaded <img> so it isn't discarded and repainted
            // (a visible flash) on every quantity click.
            const oldImg = existing.querySelector("img");
            const newImg = clone.querySelector("img");
            if (
              oldImg &&
              newImg &&
              oldImg.currentSrc &&
              oldImg.getAttribute("src") === newImg.getAttribute("src") &&
              oldImg.getAttribute("srcset") === newImg.getAttribute("srcset")
            ) {
              newImg.replaceWith(oldImg);
            }
            fragment.appendChild(clone);
          } else {
            fragment.appendChild(existing);
          }

          currMap.delete(keyVal);
        }

        // Remove leftovers
        currMap.forEach((node) => node.remove());

        requestAnimationFrame(() => {
          currTbody.innerHTML = "";
          currTbody.appendChild(fragment);
        });
      }

      // Reward progress (cheap replace)
      const newReward = doc.querySelector(".cart-reward-progress-wrapper");
      const existingReward = document.querySelector(
        ".cart-reward-progress-wrapper",
      );

      if (newReward && existingReward) {
        requestAnimationFrame(() => {
          existingReward.replaceWith(newReward.cloneNode(true));
        });
      }

      /*const newFooter2 = doc.querySelector(".cart-drawer__footer");
      const existingFooter2 = document.querySelector(".cart-drawer__footer");
      if (newFooter2 && existingFooter2) {
        requestAnimationFrame(() => {
          existingFooter2.replaceWith(newFooter2.cloneNode(true));
        });
      }*/

      document.dispatchEvent(new CustomEvent("cart:drawer:updated"));
      cartUpdated = true;
    }
  }

  function applyEmptyState(parsedState) {
    if (!parsedState || typeof parsedState.item_count === "undefined") return;
    const isEmpty = Number(parsedState.item_count) === 0;
    const drawerInner = document.querySelector(".drawer__inner-empty");
    const cartItems = document.querySelector("cart-items");
    const cartDrawerWrapper = document.querySelector("cart-drawer");
    const cartFooter = document.getElementById("main-cart-footer");
    const cdi = document.querySelector("cart-drawer-items");

    if (cartDrawerWrapper)
      cartDrawerWrapper.classList.toggle("is-empty", isEmpty);
    if (cartItems) cartItems.classList.toggle("is-empty", isEmpty);
    if (cartFooter) cartFooter.classList.toggle("is-empty", isEmpty);
    if (drawerInner) drawerInner.classList.toggle("hidden", !isEmpty);
    if (cdi) cdi.classList.toggle("is-empty", isEmpty);
    // just in case, update bubble
    if (isEmpty) updateCartBubble(0);
  }

  function ensureMutationGate() {
    if (state.mutationGate) return state.mutationGate;

    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });

    state.mutationGate = { promise, resolve };
    return state.mutationGate;
  }

  function incrementMutationCount() {
    const wasZero = state.pendingMutationCount === 0;
    state.pendingMutationCount += 1;
    state.cartVersion += 1;
    if (wasZero) ensureMutationGate();
  }

  function decrementMutationCount() {
    state.pendingMutationCount = Math.max(0, state.pendingMutationCount - 1);

    if (state.pendingMutationCount === 0 && state.mutationGate) {
      state.mutationGate.resolve();
      state.mutationGate = null;
    }
  }

  async function fetchAndCacheCart() {
    if (state.cartSnapshot) {
      return snapshotToResponse(state.cartSnapshot);
    }

    if (state.pendingMutationCount > 0 && state.mutationGate) {
      await Promise.race([
        state.mutationGate.promise,
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    }

    if (state.cartSnapshot) {
      return snapshotToResponse(state.cartSnapshot);
    }

    if (!state.cartInflight) {
      const requestVersion = state.cartVersion;
      state.cartInflight = nativeFetch("/cart.js")
        .then(async (response) => {
          const snapshot = await readSnapshot(response);
          if (
            requestVersion === state.cartVersion &&
            state.pendingMutationCount === 0
          ) {
            updateCartSnapshotFromJson(snapshot.parsedJson, snapshot);
            applyEmptyState(snapshot.parsedJson);
            if (!state.cartSnapshot) {
              // Ensure we don't persist `sections` if the raw snapshot is used
              if (
                snapshot &&
                snapshot.parsedJson &&
                snapshot.parsedJson.sections
              ) {
                const sanitizedParsed = stripSectionsFromJson(
                  snapshot.parsedJson,
                );
                state.cartSnapshot = {
                  bodyText: JSON.stringify(sanitizedParsed),
                  parsedJson: sanitizedParsed,
                  status: snapshot.status ?? 200,
                  statusText: snapshot.statusText ?? "",
                  headers: snapshot.headers || {},
                };
              } else {
                state.cartSnapshot = snapshot;
              }
            }
          }
          return snapshot;
        })
        .finally(() => {
          state.cartInflight = null;
        });
    }

    const snapshot = await state.cartInflight;
    if (snapshot.parsedJson) {
      updateCartSnapshotFromJson(snapshot.parsedJson, snapshot);
      applyEmptyState(snapshot.parsedJson);
    }
    return snapshotToResponse(state.cartSnapshot || snapshot);
  }

  // Properties global.js writes onto a line after the fact to mirror its
  // discount state for the checkout page. Shopify only merges an add into an
  // existing cart line when the variant id and *every* property match
  // exactly, so a plain "add 1 more" (sent without these keys) would
  // otherwise land as a brand-new line instead of merging quantity into the
  // line that already carries them.
  const CART_SYNC_PROPERTY_KEYS = [
    "_checkoutPageDiscountCodes",
    "_checkoutPageOrderDiscounts",
  ];

  function propsSignatureExcluding(props, excludeKeys) {
    if (!props || typeof props !== "object") return "";
    let str = "";
    for (const key of Object.keys(props).sort()) {
      if (excludeKeys.includes(key)) continue;
      str += key + ":" + props[key] + "|";
    }
    return str;
  }

  // Copy an already-in-cart line's sync properties onto a new add for the
  // same variant/properties so the identity matches and Shopify merges the
  // quantity into that line instead of creating a duplicate one.
  function enrichWithExistingLineSyncProperties(item) {
    const cartItems = state.cartSnapshot?.parsedJson?.items;
    if (!Array.isArray(cartItems) || !cartItems.length) return item;

    const itemId = item.id ?? item.variant_id;
    if (itemId == null) return item;

    const alreadyHasSyncProps = CART_SYNC_PROPERTY_KEYS.some(
      (key) => item.properties && item.properties[key] != null,
    );
    if (alreadyHasSyncProps) return item;

    const otherPropsSig = propsSignatureExcluding(
      item.properties,
      CART_SYNC_PROPERTY_KEYS,
    );

    const match = cartItems.find(
      (cartItem) =>
        String(cartItem.variant_id ?? cartItem.id) === String(itemId) &&
        propsSignatureExcluding(cartItem.properties, CART_SYNC_PROPERTY_KEYS) ===
          otherPropsSig,
    );
    if (!match || !match.properties) return item;

    const syncProps = {};
    CART_SYNC_PROPERTY_KEYS.forEach((key) => {
      if (match.properties[key] != null) syncProps[key] = match.properties[key];
    });
    if (!Object.keys(syncProps).length) return item;

    return { ...item, properties: { ...item.properties, ...syncProps } };
  }

  function mergeAddEntry(target, rawItem) {
    const item = enrichWithExistingLineSyncProperties(rawItem);
    const identity = signature(item);

    const existing = target.map.get(identity);
    const quantity = item.quantity == null ? 1 : +item.quantity;
    if (existing) {
      existing.quantity += quantity;
      return;
    }

    target.map.set(identity, {
      ...item,
      quantity,
    });
  }

  function mergeAddPayload(target, payload) {
    const sections = normalizeSections(payload.sections);
    sections.forEach((section) => target.sections.add(section));
    if (payload.sections_url) target.sections_url = payload.sections_url;

    if (Array.isArray(payload.items) && payload.items.length) {
      payload.items.forEach((item) => mergeAddEntry(target, item));
      return;
    }

    const item = {};
    if (payload.id != null) item.id = payload.id;
    if (payload.variant_id != null) item.id = payload.variant_id;
    if (payload.quantity != null) item.quantity = Number(payload.quantity);
    if (payload.selling_plan != null) item.selling_plan = payload.selling_plan;
    if (payload.properties) item.properties = payload.properties;

    if (Object.keys(item).length) mergeAddEntry(target, item);
  }

  function buildAddRequest(queue) {
    const merged = {
      map: new Map(),
      sections: new Set(),
      sections_url: "",
    };

    queue.forEach((entry) => mergeAddPayload(merged, entry.payload));

    const body = new FormData();
    const items = [...merged.map.values()];

    // When only a single item ends up in the batch (the common case — most
    // adds never collide with another queued add), send it in the same flat
    // shape Shopify's native /cart/add.js expects for a singular add. This
    // keeps the response in the flat single-item shape too, which matters
    // for any code (including Shopify's own cart-add tracking) that reads
    // top-level fields off the response instead of an `items[]` array.
    const useFlatShape = items.length === 1;

    items.forEach((item, index) => {
      const prefix = useFlatShape ? "" : `items[${index}]`;
      body.append(prefix ? `${prefix}[id]` : "id", String(item.id));
      body.append(
        prefix ? `${prefix}[quantity]` : "quantity",
        String(item.quantity || 1),
      );

      if (item.selling_plan != null) {
        body.append(
          prefix ? `${prefix}[selling_plan]` : "selling_plan",
          String(item.selling_plan),
        );
      }

      if (item.properties && isPlainObject(item.properties)) {
        Object.keys(item.properties).forEach((key) => {
          body.append(
            prefix
              ? `${prefix}[properties][${key}]`
              : `properties[${key}]`,
            String(item.properties[key]),
          );
        });
      }
    });

    if (merged.sections.size) {
      const list = [...merged.sections];
      const sanitized = isCartPage ? sanitizeSectionsList(list) : list;
      if (sanitized.length) body.append("sections", sanitized.join(","));
    }

    if (merged.sections_url) {
      body.append("sections_url", merged.sections_url);
    }

    const firstInit = queue[0]?.init || {};
    const headers = new Headers(firstInit.headers || {});
    headers.delete("Content-Type");

    return { body, init: { ...firstInit, method: "POST", headers } };
  }

  function queueAddMutation(input, init, payload) {
    incrementMutationCount();

    // Some callers (e.g. third-party app scripts) POST to /cart/add.js
    // without requesting any sections at all, which leaves the cart drawer
    // and cart icon stale after their add. If nothing in this payload asked
    // for sections, fall back to the same defaults the theme's own
    // add-to-cart flows request.
    if (
      payload &&
      !normalizeSections(payload.sections).length &&
      !payload.sections_url
    ) {
      if (isCartPage) {
        const { itemsKey, footerKey } = getCartSectionKeys();
        payload.sections = [itemsKey, footerKey, "cart-icon-bubble"];
      } else {
        payload.sections = ["cart-drawer", "cart-icon-bubble"];
      }
    }

    return new Promise((resolve, reject) => {
      state.addQueue.push({ input, init, payload, resolve, reject });
      if (!state.addTimer) {
        const delay = state.batchWindowMs || MICRO_BATCH_MS;
        state.addTimer = setTimeout(() => {
          flushAddQueue();
        }, delay);
      }
    });
  }

  async function flushAddQueue() {
    // grab current queued entries
    let queue = state.addQueue.splice(0);
    if (!queue.length) return;

    let { body, init } = buildAddRequest(queue);

    try {
      if (shouldAppendSections(init)) {
        const temp = { body };
        safelyAppendSections(temp);
        body = temp.body;
      }

      const response = await nativeFetch(queue[0].input || "/cart/add.js", {
        ...init,
        body,
      });
      const snapshot = await readSnapshot(response);

      if (snapshot.parsedJson) {
        if (isCartSnapshot(snapshot.parsedJson)) {
          updateCartSnapshotFromJson(snapshot.parsedJson, snapshot);
          applyEmptyState(snapshot.parsedJson);
        } else if (
          Array.isArray(snapshot.parsedJson) ||
          (snapshot.parsedJson.items &&
            Array.isArray(snapshot.parsedJson.items))
        ) {
          const itemsArr = Array.isArray(snapshot.parsedJson)
            ? snapshot.parsedJson
            : snapshot.parsedJson.items;
          appendItemsToSnapshot(itemsArr, snapshot);
        } else if (isSingleAddedItem(snapshot.parsedJson)) {
          appendItemsToSnapshot([snapshot.parsedJson], snapshot);
        }

        if (snapshot.parsedJson && snapshot.parsedJson.sections) {
          updateCartDrawerFromSections(snapshot.parsedJson.sections);
          scheduleCartTotalUpdate(snapshot.parsedJson);
        }
      }

      // Give each queued caller its own real Response instance — a Response
      // body can only be consumed once, so a shared instance can't be reused
      // across multiple consumers the way the old fake object could.
      queue.forEach((entry) => entry.resolve(toResponse(snapshot)));
      // Dispatch a compatibility event so listeners expecting
      // `cart:item:added` on native add calls still receive it.
      if (snapshot.status >= 200 && snapshot.status < 300) {
        setTimeout(() => {
          document.dispatchEvent(new Event("cart:item:added"));
        }, 0);
      }
    } catch (error) {
      queue.forEach((entry) => entry.reject(error));
    } finally {
      queue.forEach(() => decrementMutationCount());
    }
    // allow scheduling a new batch after this flush completes
    state.addTimer = null;
  }

  function mergeUpdatePayload(target, payload) {
    const sections = normalizeSections(payload.sections);
    sections.forEach((section) => target.sections.add(section));

    if (payload.sections_url) target.sections_url = payload.sections_url;
    if (payload.note != null) target.note = payload.note;

    if (payload.attributes && isPlainObject(payload.attributes)) {
      Object.keys(payload.attributes).forEach((key) => {
        target.attributes[key] = payload.attributes[key];
      });
    }

    if (payload.updates && isPlainObject(payload.updates)) {
      Object.keys(payload.updates).forEach((key) => {
        const nextValue = payload.updates[key];
        const nextSignature = signature(nextValue);
        if (target.updatesSeen.get(key) === nextSignature) return;
        target.updates[key] = nextValue;
        target.updatesSeen.set(key, nextSignature);
      });
    }
  }

  function buildUpdateRequest(queue) {
    const merged = {
      updates: {},
      updatesSeen: new Map(),
      attributes: {},
      sections: new Set(),
      sections_url: "",
      note: undefined,
    };

    queue.forEach((entry) => mergeUpdatePayload(merged, entry.payload));

    const body = {};
    if (Object.keys(merged.updates).length) body.updates = merged.updates;
    if (Object.keys(merged.attributes).length)
      body.attributes = merged.attributes;
    if (merged.sections.size) {
      const list = [...merged.sections];
      const sanitized = isCartPage ? sanitizeSectionsList(list) : list;
      if (sanitized.length) body.sections = sanitized;
    }
    if (merged.sections_url) body.sections_url = merged.sections_url;
    if (typeof merged.note !== "undefined") body.note = merged.note;

    const firstInit = queue[0]?.init || {};
    const headers = new Headers(firstInit.headers || {});
    headers.set("Content-Type", "application/json");

    return {
      body: JSON.stringify(body),
      init: { ...firstInit, method: "POST", headers },
    };
  }

  function queueUpdateMutation(input, init, payload) {
    incrementMutationCount();

    // If user is on the cart page, ensure update payload requests cart sections
    if (isCartPage && payload && !isPayloadOnlyAttributes(payload)) {
      const { itemsKey, footerKey } = getCartSectionKeys();
      let existing = normalizeSections(payload.sections || []);
      if (!existing.includes(footerKey)) existing.push(footerKey);
      if (!existing.includes(itemsKey)) existing.push(itemsKey);
      existing = sanitizeSectionsList(existing);
      payload.sections = existing;
    }

    return new Promise((resolve, reject) => {
      state.updateQueue.push({ input, init, payload, resolve, reject });
      if (!state.updateTimer) {
        const delay = state.batchWindowMs || MICRO_BATCH_MS;
        state.updateTimer = setTimeout(() => {
          flushUpdateQueue();
        }, delay);
      }
    });
  }

  async function flushUpdateQueue() {
    // grab current queued entries
    let queue = state.updateQueue.splice(0);
    if (!queue.length) return;

    let { body, init } = buildUpdateRequest(queue);

    try {
      if (shouldAppendSections(init)) {
        const temp = { body };
        safelyAppendSections(temp);
        body = temp.body;
      }

      const response = await nativeFetch(queue[0].input || "/cart/update.js", {
        ...init,
        body,
      });
      const snapshot = await readSnapshot(response);

      if (snapshot.parsedJson) {
        if (isCartSnapshot(snapshot.parsedJson)) {
          updateCartSnapshotFromJson(snapshot.parsedJson, snapshot);
          applyEmptyState && applyEmptyState(snapshot.parsedJson);
        } else if (
          Array.isArray(snapshot.parsedJson) ||
          (snapshot.parsedJson.items &&
            Array.isArray(snapshot.parsedJson.items))
        ) {
          const itemsArr = Array.isArray(snapshot.parsedJson)
            ? snapshot.parsedJson
            : snapshot.parsedJson.items;
          //appendItemsToSnapshot(itemsArr, snapshot);
        }

        if (snapshot.parsedJson && snapshot.parsedJson.sections) {
          updateCartDrawerFromSections(snapshot.parsedJson.sections);
          scheduleCartTotalUpdate(snapshot.parsedJson);
        }
      }

      // See note in flushAddQueue: give each queued caller its own Response.
      queue.forEach((entry) => entry.resolve(toResponse(snapshot)));
      if (snapshot.status >= 200 && snapshot.status < 300) {
        setTimeout(() => {
          document.dispatchEvent(new Event("cart:updated"));
        }, 0);
      }
    } catch (error) {
      queue.forEach((entry) => entry.reject(error));
    } finally {
      queue.forEach(() => decrementMutationCount());
    }
    // allow scheduling a new batch after this flush completes
    state.updateTimer = null;
  }

  async function fetchChange(input, init) {
    incrementMutationCount();

    try {
      if (shouldAppendSections(init)) {
        safelyAppendSections(init);
      }

      const response = await nativeFetch(input, init);
      const snapshot = await readSnapshot(response);

      if (snapshot.parsedJson) {
        handleCartSnapshot(snapshot);
      }

      return toResponse(snapshot);
    } finally {
      decrementMutationCount();
    }
  }

  /* ---------------- helpers ---------------- */

  const isCartPage = (() => {
    const p = window.location.pathname;
    return p === "/cart" || p.endsWith("/cart");
  })();

  function shouldAppendSections(init) {
    if (!init) return false;
    if (typeof window.location === "undefined") return false;
    return isCartPage;
  }

  function safelyAppendSections(init) {
    try {
      // If the body parses to a payload that only contains `attributes`,
      // do not append sections (unnecessary on-cart write of attributes).
      const parsed = parseBody(init && init.body);
      if (isPayloadOnlyAttributes(parsed)) return;

      const { itemsKey, footerKey } = getCartSectionKeys();
      const keys = [footerKey, itemsKey];

      init.body = appendSectionsToBody(init.body, keys);
    } catch {
      // best-effort
    }
  }

  function appendSectionsToBody(body, keys) {
    if (!body) return body;

    if (body instanceof FormData || body instanceof URLSearchParams) {
      const cur = body.get("sections");
      let existing = normalizeSections(cur || "");
      keys.forEach((k) => {
        const set = new Set(existing);
        for (let i = 0; i < keys.length; i++) set.add(keys[i]);
        existing = [...set];
      });
      if (isCartPage) existing = sanitizeSectionsList(existing);
      const next = existing.join(",");
      body.set("sections", next);
      return body;
    }

    if (typeof body === "string") {
      return handleStringBody(body, keys);
    }

    if (isPlainObject(body)) {
      const existing = normalizeSections(body.sections || []);
      keys.forEach((k) => {
        const set = new Set(existing);
        for (let i = 0; i < keys.length; i++) set.add(keys[i]);
        existing = [...set];
      });
      body.sections = existing;
      return body;
    }

    return body;
  }

  function handleStringBody(body, keys) {
    try {
      const parsed = JSON.parse(body);

      if (parsed && typeof parsed === "object") {
        let existing = normalizeSections(parsed.sections || []);
        keys.forEach((k) => {
          if (!existing.includes(k)) existing.push(k);
        });
        if (isCartPage) existing = sanitizeSectionsList(existing);
        parsed.sections = existing;
        return JSON.stringify(parsed);
      }
    } catch {
      // fall through
    }

    const params = new URLSearchParams(body);
    const cur = params.get("sections");
    let existing = normalizeSections(cur || "");
    keys.forEach((k) => {
      if (!existing.includes(k)) existing.push(k);
    });
    if (isCartPage) existing = sanitizeSectionsList(existing);
    const next = existing.join(",");
    params.set("sections", next);
    return params.toString();
  }

  function handleCartSnapshot(snapshot) {
    const json = snapshot.parsedJson;
    if (isCartSnapshot(json)) {
      updateCartSnapshotFromJson(json, snapshot);
    }

    const hasQueuedSections = hasQueuedSectionUpdates();
    if (json.sections && !hasQueuedSections) {
      updateCartDrawerFromSections(json.sections);
      scheduleCartTotalUpdate(json);
    }
    applyEmptyState?.(json);
  }

  function hasQueuedSectionUpdates() {
    return (
      Array.isArray(state.updateQueue) &&
      state.updateQueue.some((entry) => {
        const p = entry?.payload;
        return p?.sections || p?.sections_url;
      })
    );
  }

  async function fetchCart() {
    const response = await fetchAndCacheCart();
    // Reuse the same response-like object to avoid per-consumer clone allocations.
    // Consumers can call `.clone()` if they need a separate instance.
    return response;
  }

  function cartFetch(input, init = {}) {
    const url = normalizeUrl(input);
    const method = String(
      init.method || (input && input.method) || "GET",
    ).toUpperCase();
    const cartRoute = getCartRoute(url, method);

    if (!cartRoute) {
      return nativeFetch(input, init);
    }

    if (cartRoute === "cart") {
      return fetchCart(input, init);
    }

    if (cartRoute === "add") {
      return queueAddMutation(input, init, parseBody(init.body));
    }

    if (cartRoute === "update") {
      return queueUpdateMutation(input, init, parseBody(init.body));
    }

    if (cartRoute === "change") {
      return fetchChange(input, init);
    }

    return nativeFetch(input, init);
  }

  window.CartRequestManager = {
    getCart: fetchCart,
    invalidateCache() {
      state.cartSnapshot = null;
    },
    _state: state,
  };

  window.fetch = cartFetch;
})();
